import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useConfirmation } from '../components/ui/ConfirmationDialog';
import { apiClient } from '../lib/api/client';
import { Candidate } from '../lib/types/api';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { ResponsiveContainer } from '../components/ui/ResponsiveContainer';
import { useJobs } from '../contexts/JobContext';
import { 
  ArrowLeft, 
  Mail, 
  Phone, 
  MapPin, 
  Briefcase, 
  GraduationCap, 
  Calendar,
  FileText,
  Download,
  Star,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  BarChart3,
  PhoneCall
} from 'lucide-react';
import { ReportDisplay } from '../components/calls/ReportDisplay';
import { GenerateCallAnalysisButton } from '../components/calls/GenerateCallAnalysisButton';

const getPrimaryRequirementId = (candidate?: Candidate | null): string | undefined => {
  if (!candidate) return undefined;

  const direct = (candidate as any)?.requirement_id;
  if (typeof direct === 'string' && direct.trim().length > 0) {
    return direct;
  }

  const fromSubmissions = candidate.submissions?.find((submission: any) => {
    return typeof submission?.requirement_id === 'string' && submission.requirement_id.trim().length > 0;
  })?.requirement_id;
  if (fromSubmissions) {
    return fromSubmissions;
  }

  const fromAnalysis = (candidate as any)?.analysis_results?.requirement_id;
  if (typeof fromAnalysis === 'string' && fromAnalysis.trim().length > 0) {
    return fromAnalysis;
  }

  return undefined;
};

const getPrimaryRequirementTitle = (candidate?: Candidate | null, requirementId?: string): string | undefined => {
  if (!candidate) return undefined;

  // First try to find from submissions matching the requirement ID
  const submission = candidate.submissions?.find((sub: any) => 
    sub?.requirement_id === requirementId || !requirementId
  );
  if (submission?.requirement_title) {
    return submission.requirement_title;
  }

  // Fallback to first submission with a title
  const firstWithTitle = candidate.submissions?.find((sub: any) => sub?.requirement_title);
  if (firstWithTitle?.requirement_title) {
    return firstWithTitle.requirement_title;
  }

  return undefined;
};

const CandidateDetails: React.FC = () => {
  const { candidateId } = useParams<{ candidateId: string }>();
  const navigate = useNavigate();
  const { confirm } = useConfirmation();
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { jobs } = useJobs();
  // callCount removed - GenerateReportButton now fetches its own call count
  const [reports, setReports] = useState<any[]>([]);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);

  useEffect(() => {
    if (candidateId) {
      fetchCandidateDetails();
      fetchReports();
    }
  }, [candidateId]);

  useEffect(() => {
    if (candidateId && candidate) {
      fetchCallCount();
    }
  }, [candidateId, candidate]);

  const fetchCallCount = async () => {
    if (!candidateId || !candidate) {
      console.log('⚠️ Cannot fetch call count: missing candidateId or candidate');
      return;
    }
    
    const requirementId = getPrimaryRequirementId(candidate);
    
    if (!requirementId) {
      console.log('⚠️ Cannot fetch call count: no requirement_id found');
      // No need to fetch call count - GenerateReportButton handles this
      return;
    }
    // Call count fetching removed - GenerateReportButton now handles this internally
  };

  const fetchReports = async () => {
    if (!candidateId) return;
    try {
      const response = await apiClient.getCandidateReports(candidateId);
      if (response.success) {
        setReports(response.reports || []);
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
    }
  };

  const fetchCandidateDetails = async () => {
    if (!candidateId) return;
    
    try {
      setLoading(true);
      const response = await apiClient.getCandidate(candidateId);
      setCandidate(response);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch candidate details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-success-400" />;
      case 'processing':
        return <Clock className="w-4 h-4 text-primary-400 animate-pulse" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-error-400" />;
      default:
        return <AlertCircle className="w-4 h-4 text-warning-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-gradient-to-r from-success-500/20 to-success-600/20 text-success-300 border-success-500/30';
      case 'processing':
        return 'bg-gradient-to-r from-primary-500/20 to-primary-600/20 text-primary-300 border-primary-500/30';
      case 'failed':
        return 'bg-gradient-to-r from-error-500/20 to-error-600/20 text-error-300 border-error-500/30';
      default:
        return 'bg-gradient-to-r from-warning-500/20 to-warning-600/20 text-warning-300 border-warning-500/30';
    }
  };

  if (loading) {
    return (
      <ResponsiveContainer>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" variant="ai" />
        </div>
      </ResponsiveContainer>
    );
  }

  if (error || !candidate) {
    return (
      <ResponsiveContainer>
        <div className="text-center py-12">
          <div className="text-error-400 mb-4">{error || 'Candidate not found'}</div>
          <Button onClick={() => navigate('/candidates')} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Candidates
          </Button>
        </div>
      </ResponsiveContainer>
    );
  }

  const analysisResults = candidate.analysis_results;
  const formData = analysisResults?.form_data || candidate.parsed_fields || {};
  const primaryRequirementId = getPrimaryRequirementId(candidate);

  const handleDownloadResume = async () => {
    try {
      let url: string | null = null;
      try {
        const res = await apiClient.getCandidateResumeUrl(candidateId!, { disposition: 'attachment', requirementId: primaryRequirementId });
        url = res.url;
      } catch (e) {
        try {
          const res2 = await apiClient.getCandidateResumeUrl(candidateId!, { disposition: 'attachment' });
          url = res2.url;
        } catch {
          const subs: any[] = ((candidate as any)?.submissions || []) as any[];
          for (const s of subs) {
            try {
              const rr = await apiClient.getCandidateResumeUrl(candidateId!, { disposition: 'attachment', requirementId: s?.requirement_id });
              url = rr.url;
              break;
            } catch {}
          }
        }
      }
      if (!url) throw new Error('Resume URL not found');
      const a = document.createElement('a');
      a.href = url;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      console.error('Failed to get resume URL', e);
      confirm({
        title: 'Download Failed',
        description: 'Failed to download resume. Please try again.',
        variant: 'warning',
        confirmText: 'OK',
        onConfirm: () => {}
      });
    }
  };

  return (
    <ResponsiveContainer>
      <div className="space-y-6">
        {/* Header */}
        <div className="glass-card p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/candidates')}
                className="p-2"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-text-enterprise-primary">
                  {candidate.name}
                </h1>
                <p className="text-text-enterprise-secondary">
                  Candidate Profile & Analysis
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-2 w-full sm:w-auto">
              {/* Resume banner if there is an active job for this candidate */}
              {jobs.some(j=> j.anchors?.candidate_id === candidateId && j.status !== 'completed' && j.status !== 'failed') && (
                <div className="px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-400 dark:text-blue-300 text-sm">
                  Analysis running… You can leave this page; use Rejoin to continue.
                </div>
              )}
              
              {/* All Action Buttons in One Row */}
              <div className="flex flex-wrap items-center gap-2">
                {/* View Analysis - requires requirementId */}
                {primaryRequirementId ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/candidates/${candidateId}/analysis/${primaryRequirementId}`)}
                    className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium"
                  >
                    <FileText className="w-4 h-4 mr-1.5" />
                    View Analysis
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled
                    className="border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500 opacity-50 cursor-not-allowed"
                    title="No requirement linked"
                  >
                    <FileText className="w-4 h-4 mr-1.5" />
                    View Analysis
                  </Button>
                )}
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/candidates/${candidateId}/calls`)}
                  className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium"
                >
                  <PhoneCall className="w-4 h-4 mr-1.5" />
                  Calls
                </Button>
                
                {candidate.cv_path && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleDownloadResume} 
                    className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium"
                  >
                    <Download className="w-4 h-4 mr-1.5" />
                    Resume
                  </Button>
                )}
                
                {/* Call Analysis Button */}
                {primaryRequirementId && (
                  <GenerateCallAnalysisButton
                    candidateId={candidateId!}
                    requirementId={primaryRequirementId}
                    candidateName={candidate.name}
                    requirementTitle={getPrimaryRequirementTitle(candidate, primaryRequirementId)}
                    variant="outline"
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Basic Information */}
          <div className="lg:col-span-1 space-y-6">
            {/* Profile Card */}
            <div className="glass-card p-6">
              <div className="text-center mb-6">
                <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-primary-500/20 via-primary-600/30 to-secondary-500/20 rounded-full flex items-center justify-center border border-primary-500/30">
                  <span className="text-primary-400 font-bold text-2xl">
                    {candidate.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <h2 className="text-xl font-semibold text-text-enterprise-primary mb-1">
                  {candidate.name}
                </h2>
                <p className="text-text-enterprise-secondary">
                  {formData.current_position || 'Position not specified'}
                </p>
              </div>

              {/* Contact Information */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-text-enterprise-tertiary" />
                  <span className="text-text-enterprise-secondary text-sm">
                    {candidate.email}
                  </span>
                </div>
                {candidate.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-text-enterprise-tertiary" />
                    <span className="text-text-enterprise-secondary text-sm">
                      {candidate.phone}
                    </span>
                  </div>
                )}
                {formData.location && (
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-text-enterprise-tertiary" />
                    <span className="text-text-enterprise-secondary text-sm">
                      {formData.location}
                    </span>
                  </div>
                )}
                {formData.current_company && (
                  <div className="flex items-center gap-3">
                    <Briefcase className="w-4 h-4 text-text-enterprise-tertiary" />
                    <span className="text-text-enterprise-secondary text-sm">
                      {formData.current_company}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* CV Status */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold text-text-enterprise-primary mb-4">
                CV Status
              </h3>
              <div className="flex items-center gap-3">
                {getStatusIcon(candidate.status || 'pending')}
                <span className={`inline-flex items-center px-3 py-2 rounded-full text-sm font-medium border ${getStatusColor(candidate.status || 'pending')}`}>
                  {candidate.status === 'completed' ? '✓ Processed' :
                   candidate.status === 'processing' ? 'Processing...' :
                   candidate.status === 'failed' ? '✗ Failed' : '⏳ Pending'}
                </span>
              </div>
              {candidate.message && (
                <p className="text-text-enterprise-tertiary text-sm mt-2">
                  {candidate.message}
                </p>
              )}
            </div>
          </div>

          {/* Right Column - Detailed Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Experience & Education */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold text-text-enterprise-primary mb-6">
                Experience & Education
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-4 h-4 text-primary-400" />
                    <span className="font-medium text-text-enterprise-primary">Experience</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-text-enterprise-tertiary text-sm">Years</span>
                      <span className="text-text-enterprise-secondary text-sm">
                        {formData.experience_years || 'Not specified'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-enterprise-tertiary text-sm">Current Role</span>
                      <span className="text-text-enterprise-secondary text-sm">
                        {formData.current_position || 'Not specified'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-enterprise-tertiary text-sm">Company</span>
                      <span className="text-text-enterprise-secondary text-sm">
                        {formData.current_company || 'Not specified'}
                      </span>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <GraduationCap className="w-4 h-4 text-primary-400" />
                    <span className="font-medium text-text-enterprise-primary">Education</span>
                  </div>
                  <div className="text-text-enterprise-secondary text-sm">
                    {formData.education || 'Not specified'}
                  </div>
                </div>
              </div>
            </div>

            {/* Skills */}
            {formData.skills && formData.skills.length > 0 && (
              <div className="glass-card p-6">
                <h3 className="text-lg font-semibold text-text-enterprise-primary mb-4">
                  Skills
                </h3>
                <div className="flex flex-wrap gap-2">
                  {formData.skills.map((skill: string, index: number) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-gradient-to-r from-primary-500/20 to-secondary-500/20 text-primary-300 text-sm rounded-full border border-primary-500/30"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Analysis Summary */}
            {analysisResults && (
              <div className="glass-card p-6">
                <h3 className="text-lg font-semibold text-text-enterprise-primary mb-4">
                  Analysis Summary
                </h3>
                {analysisResults.analysis && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                    <div className="text-center p-3 bg-glass-surface/30 rounded-lg">
                      <div className="text-2xl font-bold text-primary-400">
                        {analysisResults.analysis.overall_fitment_score || 0}%
                      </div>
                      <div className="text-xs text-text-enterprise-tertiary">Overall Fit</div>
                    </div>
                    <div className="text-center p-3 bg-glass-surface/30 rounded-lg">
                      <div className="text-2xl font-bold text-primary-400">
                        {analysisResults.analysis.skill_match_score || 0}%
                      </div>
                      <div className="text-xs text-text-enterprise-tertiary">Skills Match</div>
                    </div>
                    <div className="text-center p-3 bg-glass-surface/30 rounded-lg">
                      <div className="text-2xl font-bold text-primary-400">
                        {analysisResults.analysis.experience_match_score || 0}%
                      </div>
                      <div className="text-xs text-text-enterprise-tertiary">Experience</div>
                    </div>
                  </div>
                )}
                {analysisResults.recommendation && (
                  <div className="p-4 bg-glass-surface/20 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Star className="w-4 h-4 text-warning-400" />
                      <span className="font-medium text-text-enterprise-primary">Recommendation</span>
                    </div>
                    <p className="text-text-enterprise-secondary text-sm">
                      {analysisResults.recommendation.overall_recommendation || 'No recommendation available'}
                    </p>
                  </div>
                )}
              </div>
            )}

      {/* JD vs CV Analysis Table */}
      {candidate.submissions && candidate.submissions.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            JD vs CV Analysis
          </h3>
          
          {/* Desktop Table View */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Requirement
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Fitment Score
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {candidate.submissions.map((submission, index) => {
                  // Get fitment score - check analysis_results first (more reliable), then fallback
                  const analysisScore = (submission as any).analysis_results?.analysis?.overall_fitment_score;
                  const directScore = submission.fitment_score;
                  
                  // Use analysis_results score if available and > 0, otherwise use direct score if > 0
                  let fitmentScore: number | null = null;
                  let hasValidAnalysis = false;
                  
                  if (analysisScore !== undefined && analysisScore !== null && analysisScore > 0) {
                    fitmentScore = analysisScore;
                    hasValidAnalysis = true;
                  } else if (directScore !== undefined && directScore !== null && directScore > 0) {
                    fitmentScore = directScore;
                    hasValidAnalysis = true;
                  } else if ((submission as any).analysis_results?.analysis) {
                    // Analysis exists but score is 0 - show 0%
                    fitmentScore = analysisScore ?? directScore ?? 0;
                    hasValidAnalysis = true;
                  }
                  
                  return (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {submission.requirement_title || 'Untitled Requirement'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full ${
                          submission.submission_status === 'submitted' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                        }`}>
                          {submission.submission_status || 'pending'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                        {submission.submitted_at ? new Date(submission.submitted_at).toLocaleDateString() : '--'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {hasValidAnalysis && fitmentScore !== null ? (
                          <div className={`text-lg font-bold ${
                            fitmentScore >= 80 ? 'text-green-600 dark:text-green-400' :
                            fitmentScore >= 60 ? 'text-yellow-600 dark:text-yellow-400' :
                            fitmentScore > 0 ? 'text-orange-600 dark:text-orange-400' :
                            'text-gray-500 dark:text-gray-400'
                          }`}>
                            {Math.round(fitmentScore)}%
                          </div>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500 text-sm italic">Pending</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/candidates/${candidate.id}/analysis/${submission.requirement_id}`)}
                          className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <FileText className="w-4 h-4 mr-1.5" />
                          View
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden space-y-3">
            {candidate.submissions.map((submission, index) => {
              // Same logic as desktop
              const analysisScore = (submission as any).analysis_results?.analysis?.overall_fitment_score;
              const directScore = submission.fitment_score;
              
              let fitmentScore: number | null = null;
              let hasValidAnalysis = false;
              
              if (analysisScore !== undefined && analysisScore !== null && analysisScore > 0) {
                fitmentScore = analysisScore;
                hasValidAnalysis = true;
              } else if (directScore !== undefined && directScore !== null && directScore > 0) {
                fitmentScore = directScore;
                hasValidAnalysis = true;
              } else if ((submission as any).analysis_results?.analysis) {
                fitmentScore = analysisScore ?? directScore ?? 0;
                hasValidAnalysis = true;
              }
              
              return (
                <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0 pr-3">
                      <h4 className="font-medium text-gray-900 dark:text-white text-sm truncate">
                        {submission.requirement_title || 'Untitled Requirement'}
                      </h4>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                          submission.submission_status === 'submitted' 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                        }`}>
                          {submission.submission_status || 'pending'}
                        </span>
                        {submission.submitted_at && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(submission.submitted_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-center flex-shrink-0">
                      {hasValidAnalysis && fitmentScore !== null ? (
                        <>
                          <div className={`text-xl font-bold ${
                            fitmentScore >= 80 ? 'text-green-600 dark:text-green-400' :
                            fitmentScore >= 60 ? 'text-yellow-600 dark:text-yellow-400' :
                            fitmentScore > 0 ? 'text-orange-600 dark:text-orange-400' :
                            'text-gray-500 dark:text-gray-400'
                          }`}>
                            {Math.round(fitmentScore)}%
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">Fitment</div>
                        </>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500 text-sm italic">Pending</span>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/candidates/${candidate.id}/analysis/${submission.requirement_id}`)}
                    className="w-full border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <FileText className="w-4 h-4 mr-1.5" />
                    View Analysis
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

            {/* Call Reports */}
            {reports.length > 0 && (
              <div className="glass-card p-6">
                <h3 className="text-lg font-semibold text-text-enterprise-primary mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary-400" />
                  Call Analysis Reports
                </h3>
                <div className="space-y-3">
                  {reports.map((report) => (
                    <div
                      key={report.report_id}
                      className="p-4 bg-glass-surface/30 rounded-lg border border-glass-border/30 hover:bg-glass-surface/50 transition-colors cursor-pointer"
                      onClick={() => setSelectedReportId(report.report_id)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-text-enterprise-primary">
                            Report {new Date(report.created_at).toLocaleDateString()}
                          </div>
                          <div className="text-sm text-text-enterprise-secondary">
                            {report.call_count} calls • {report.status}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {report.status === 'completed' && (
                            <CheckCircle className="w-5 h-5 text-success-400" />
                          )}
                          {report.status === 'processing' && (
                            <Clock className="w-5 h-5 text-primary-400 animate-pulse" />
                          )}
                          {report.status === 'failed' && (
                            <XCircle className="w-5 h-5 text-error-400" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Timeline */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold text-text-enterprise-primary mb-4">
                Timeline
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-text-enterprise-tertiary" />
                  <div>
                    <div className="text-text-enterprise-secondary text-sm">Created</div>
                    <div className="text-text-enterprise-primary text-sm">
                      {new Date(candidate.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-text-enterprise-tertiary" />
                  <div>
                    <div className="text-text-enterprise-secondary text-sm">Last Updated</div>
                    <div className="text-text-enterprise-primary text-sm">
                      {new Date(candidate.updated_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                {candidate.gdpr_consent_at && (
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-4 h-4 text-success-400" />
                    <div>
                      <div className="text-text-enterprise-secondary text-sm">GDPR Consent</div>
                      <div className="text-text-enterprise-primary text-sm">
                        {new Date(candidate.gdpr_consent_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Report Display Modal */}
      {selectedReportId && (
        <ReportDisplay
          reportId={selectedReportId}
          isOpen={!!selectedReportId}
          onClose={() => setSelectedReportId(null)}
        />
      )}
    </ResponsiveContainer>
  );
};

export default CandidateDetails;
